import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ECharts } from 'echarts'
import { getRulerPlotRect, type RulerPlotRect } from './chartRulerGeometry'
import { chartCandleDistanceAt, type CandleGeometry } from './candleHitTest'

// Every screener chart participates; starting a ruler replaces the previous one.
const RULER_START_EVENT = 'screener:ruler-start'

export interface RulerSeries {
  id: string
  label: string
  color: string
  /** Timestamps are seconds, matching the screener API. */
  points: Array<{ timestamp: number; value: number }>
  candleGeometry?: readonly CandleGeometry[]
}

export interface RulerSelection {
  seriesId: string
  /** Free axis coordinates; samples inside this time range supply the OI details. */
  startTimestamp: number
  endTimestamp: number
  startValue: number
  endValue: number
}

interface PixelPoint { x: number; y: number }

export interface RulerGeometry {
  /** Chart-local coordinates for an SVG overlay. */
  start: PixelPoint
  end: PixelPoint
  startValue: number
  endValue: number
  plot: RulerPlotRect
  width: number
  height: number
  /** First selected point in viewport coordinates; the popup must not follow the endpoint. */
  anchor: PixelPoint
}

interface RulerState {
  selection: RulerSelection | null
  measuring: boolean
  geometry: RulerGeometry | null
}

function nearestPoint(points: RulerSeries['points'], timestamp: number) {
  if (!points.length) return null
  let low = 0
  let high = points.length
  while (low < high) {
    const middle = (low + high) >>> 1
    if (points[middle].timestamp < timestamp) low = middle + 1
    else high = middle
  }
  if (!low) return points[0]
  if (low === points.length) return points[low - 1]
  return timestamp - points[low - 1].timestamp <= points[low].timestamp - timestamp
    ? points[low - 1]
    : points[low]
}

function sameGeometry(a: RulerGeometry | null, b: RulerGeometry | null) {
  return a === b || (!!a && !!b
    && a.start.x === b.start.x && a.start.y === b.start.y
    && a.end.x === b.end.x && a.end.y === b.end.y
    && a.startValue === b.startValue && a.endValue === b.endValue
    && a.plot.x === b.plot.x && a.plot.y === b.plot.y
    && a.plot.width === b.plot.width && a.plot.height === b.plot.height
    && a.width === b.width && a.height === b.height
    && a.anchor.x === b.anchor.x && a.anchor.y === b.anchor.y)
}

/** Shift-click/click or Shift-drag to measure without starting ECharts' pan gesture. */
export function useChartRuler({ chart, series, resetKey }: {
  /** Pass the final instance received by ReactECharts' onChartReady callback. */
  chart: ECharts | null
  series: RulerSeries[]
  resetKey: string
}) {
  const validSeries = useMemo(() => series.map((entry) => ({
    ...entry,
    points: entry.points.filter((point) => Number.isFinite(point.timestamp) && Number.isFinite(point.value))
      .sort((a, b) => a.timestamp - b.timestamp),
  })), [series])
  const seriesRef = useRef(validSeries)
  const selectionRef = useRef<RulerSelection | null>(null)
  const measuringRef = useRef(false)
  const pressOrigin = useRef<PixelPoint | null>(null)
  const swallowPress = useRef(false)
  const suppressClick = useRef(false)
  const refreshRef = useRef<(() => void) | null>(null)
  const [state, setState] = useState<RulerState>({ selection: null, measuring: false, geometry: null })

  const clear = useCallback(() => {
    selectionRef.current = null
    measuringRef.current = false
    pressOrigin.current = null
    swallowPress.current = false
    suppressClick.current = false
    setState({ selection: null, measuring: false, geometry: null })
    refreshRef.current?.()
  }, [])

  useEffect(() => {
    seriesRef.current = validSeries
    refreshRef.current?.()
  }, [validSeries])

  useEffect(() => { clear() }, [resetKey, clear])

  useEffect(() => {
    if (!chart || chart.isDisposed()) return
    const instance = chart
    let disposed = false
    let frame = 0
    let detach: (() => void) | undefined

    const attach = () => {
      const dom = instance.getDom()
      const initialCursor = dom.style.cursor
      let shiftHeld = false
      let hidingTooltip = false
      let clickResetTimer = 0

      const hideTooltip = () => {
        if (instance.isDisposed() || hidingTooltip) return
        hidingTooltip = true
        instance.dispatchAction({ type: 'hideTip' }, { silent: true })
        hidingTooltip = false
      }
      const syncCursor = () => { dom.style.cursor = measuringRef.current || shiftHeld ? 'crosshair' : initialCursor }
      const asPixel = (timestamp: number, value: number): PixelPoint | null => {
        const pixel = instance.convertToPixel({ gridIndex: 0 }, [timestamp * 1000, value])
        return Array.isArray(pixel) && pixel.every(Number.isFinite) ? { x: pixel[0], y: pixel[1] } : null
      }
      const render = () => {
        frame = 0
        if (disposed || instance.isDisposed()) return
        syncCursor()
        let geometry: RulerGeometry | null = null
        const selection = selectionRef.current
        if (selection) {
          if (!seriesRef.current.some((entry) => entry.id === selection.seriesId)) {
            clear()
            return
          }
          // Keep the selected axis coordinates through live revisions and rolling windows.
          const startPixel = asPixel(selection.startTimestamp, selection.startValue)
          const endPixel = asPixel(selection.endTimestamp, selection.endValue)
          const rect = dom.getBoundingClientRect()
          const width = instance.getWidth()
          const height = instance.getHeight()
          const plot = getRulerPlotRect(instance)
          if (startPixel && endPixel && plot && width > 0 && height > 0 && rect.width > 0 && rect.height > 0) {
            geometry = {
              start: startPixel,
              end: endPixel,
              startValue: selection.startValue,
              endValue: selection.endValue,
              plot,
              width,
              height,
              anchor: {
                x: rect.left + Math.max(0, Math.min(width, startPixel.x)) * rect.width / width,
                y: rect.top + Math.max(0, Math.min(height, startPixel.y)) * rect.height / height,
              },
            }
          }
        }
        setState((previous) => previous.selection === selection && previous.measuring === measuringRef.current
          && sameGeometry(previous.geometry, geometry)
          ? previous
          : { selection, measuring: measuringRef.current, geometry })
      }
      const refresh = () => { if (!disposed && !frame) frame = requestAnimationFrame(render) }
      refreshRef.current = refresh
      const localPoint = (event: MouseEvent): PixelPoint => {
        const rect = dom.getBoundingClientRect()
        return {
          x: (event.clientX - rect.left) * instance.getWidth() / (rect.width || 1),
          y: (event.clientY - rect.top) * instance.getHeight() / (rect.height || 1),
        }
      }
      const coordinatesAt = (point: PixelPoint): { timestamp: number; value: number } | null => {
        const plot = getRulerPlotRect(instance)
        if (!plot) return null
        // A drag released outside the chart ends at the visible plot boundary.
        const x = Math.max(plot.x, Math.min(plot.x + plot.width, point.x))
        const y = Math.max(plot.y, Math.min(plot.y + plot.height, point.y))
        const data = instance.convertFromPixel({ gridIndex: 0 }, [x, y])
        return Array.isArray(data) && Number.isFinite(data[0]) && Number.isFinite(data[1])
          ? { timestamp: data[0] / 1000, value: data[1] }
          : null
      }
      const suppress = (event: Event) => {
        event.preventDefault()
        event.stopImmediatePropagation()
      }
      const updateEnd = (point: PixelPoint) => {
        const selection = selectionRef.current
        const endpoint = coordinatesAt(point)
        if (!selection || !endpoint) return
        if (endpoint.timestamp !== selection.endTimestamp || endpoint.value !== selection.endValue) {
          selectionRef.current = { ...selection, endTimestamp: endpoint.timestamp, endValue: endpoint.value }
          refresh()
        }
      }
      const down = (event: MouseEvent) => {
        if (instance.isDisposed()) return
        if (event.button !== 0 || (!event.shiftKey && !measuringRef.current)) return
        const point = localPoint(event)
        if (!instance.containPixel({ gridIndex: 0 }, [point.x, point.y])) return
        const coordinates = coordinatesAt(point)
        if (!coordinates) return
        if (measuringRef.current) {
          updateEnd(point)
          measuringRef.current = false
          pressOrigin.current = null
        } else {
          // Select the drawn candle when available, retaining sample-based
          // measurements and the original line behavior for other callers.
          let closest: { id: string; distance: number } | null = null
          for (const entry of seriesRef.current) {
            if (entry.candleGeometry) {
              const distance = chartCandleDistanceAt(instance, entry.id, entry.candleGeometry, coordinates.timestamp * 1000, coordinates.value)
              if (distance != null && (!closest || distance < closest.distance)) closest = { id: entry.id, distance }
              continue
            }
            const sample = nearestPoint(entry.points, coordinates.timestamp)
            const pixel = sample && asPixel(sample.timestamp, sample.value)
            if (!sample || !pixel) continue
            const distance = Math.abs(pixel.y - point.y)
            if (!closest || distance < closest.distance) closest = { id: entry.id, distance }
          }
          if (!closest) return
          window.dispatchEvent(new CustomEvent(RULER_START_EVENT, { detail: dom }))
          selectionRef.current = {
            seriesId: closest.id,
            startTimestamp: coordinates.timestamp,
            endTimestamp: coordinates.timestamp,
            startValue: coordinates.value,
            endValue: coordinates.value,
          }
          measuringRef.current = true
          pressOrigin.current = { x: event.clientX, y: event.clientY }
        }
        swallowPress.current = true
        suppressClick.current = true
        suppress(event)
        hideTooltip()
        refresh()
      }
      const move = (event: MouseEvent) => {
        if (instance.isDisposed()) return
        shiftHeld = event.shiftKey
        syncCursor()
        if (!measuringRef.current && !event.shiftKey) return
        suppress(event)
        hideTooltip()
        if (measuringRef.current) updateEnd(localPoint(event))
      }
      const globalMove = (event: MouseEvent) => {
        if (pressOrigin.current && measuringRef.current && !dom.contains(event.target as Node)) move(event)
      }
      const up = (event: MouseEvent) => {
        if (instance.isDisposed()) return
        if (!swallowPress.current) return
        suppress(event)
        const origin = pressOrigin.current
        if (origin && measuringRef.current && Math.hypot(event.clientX - origin.x, event.clientY - origin.y) >= 4) {
          updateEnd(localPoint(event))
          measuringRef.current = false
        }
        pressOrigin.current = null
        swallowPress.current = false
        // A drag released outside the chart may not produce a local click.
        // Keep compatibility mouseup/click suppressed for this event turn only.
        window.clearTimeout(clickResetTimer)
        clickResetTimer = window.setTimeout(() => { suppressClick.current = false }, 0)
        refresh()
      }
      const click = (event: MouseEvent) => {
        if (instance.isDisposed()) return
        if (suppressClick.current || measuringRef.current || event.shiftKey) {
          suppress(event)
          suppressClick.current = false
        }
      }
      const wheel = (event: WheelEvent) => { if (!instance.isDisposed() && measuringRef.current) suppress(event) }
      const key = (event: KeyboardEvent) => {
        shiftHeld = event.shiftKey
        if (event.key === 'Escape' && selectionRef.current) clear()
        syncCursor()
      }
      const blur = () => {
        shiftHeld = false
        if (measuringRef.current) clear()
        syncCursor()
      }
      const tooltip = () => { if (measuringRef.current) hideTooltip() }
      const otherRulerStarted = (event: Event) => {
        if ((event as CustomEvent<HTMLElement>).detail !== dom && selectionRef.current) clear()
      }
      // Pointer events can generate compatibility mouse events. Handle only one stream,
      // but intercept both so either zrender event backend cannot start a pan.
      const pointerEvents = typeof PointerEvent !== 'undefined'
      const compatibility = (event: MouseEvent) => {
        if (instance.isDisposed()) return
        if (event.shiftKey || measuringRef.current || swallowPress.current || suppressClick.current) suppress(event)
      }
      const downName = pointerEvents ? 'pointerdown' : 'mousedown'
      const moveName = pointerEvents ? 'pointermove' : 'mousemove'
      const upName = pointerEvents ? 'pointerup' : 'mouseup'
      dom.addEventListener(downName, down, true)
      dom.addEventListener(moveName, move, true)
      dom.addEventListener('click', click, true)
      dom.addEventListener('dblclick', click, true)
      dom.addEventListener('wheel', wheel, { capture: true, passive: false })
      if (pointerEvents) {
        for (const name of ['mousedown', 'mousemove', 'mouseup'] as const) dom.addEventListener(name, compatibility, true)
      }
      window.addEventListener(moveName, globalMove, true)
      window.addEventListener(upName, up, true)
      window.addEventListener('pointercancel', blur)
      window.addEventListener('keydown', key)
      window.addEventListener('keyup', key)
      window.addEventListener('blur', blur)
      window.addEventListener('resize', refresh)
      window.addEventListener('scroll', refresh, true)
      window.addEventListener(RULER_START_EVENT, otherRulerStarted)
      instance.on('datazoom', refresh)
      instance.on('rendered', refresh)
      instance.on('finished', refresh)
      instance.on('showTip', tooltip)
      const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(refresh) : null
      observer?.observe(dom)
      refresh()
      detach = () => {
        window.clearTimeout(clickResetTimer)
        dom.style.cursor = initialCursor
        dom.removeEventListener(downName, down, true)
        dom.removeEventListener(moveName, move, true)
        dom.removeEventListener('click', click, true)
        dom.removeEventListener('dblclick', click, true)
        dom.removeEventListener('wheel', wheel, true)
        if (pointerEvents) {
          for (const name of ['mousedown', 'mousemove', 'mouseup'] as const) dom.removeEventListener(name, compatibility, true)
        }
        window.removeEventListener(moveName, globalMove, true)
        window.removeEventListener(upName, up, true)
        window.removeEventListener('pointercancel', blur)
        window.removeEventListener('keydown', key)
        window.removeEventListener('keyup', key)
        window.removeEventListener('blur', blur)
        window.removeEventListener('resize', refresh)
        window.removeEventListener('scroll', refresh, true)
        window.removeEventListener(RULER_START_EVENT, otherRulerStarted)
        instance.off('datazoom', refresh)
        instance.off('rendered', refresh)
        instance.off('finished', refresh)
        instance.off('showTip', tooltip)
        observer?.disconnect()
      }
    }
    attach()
    return () => {
      disposed = true
      cancelAnimationFrame(frame)
      refreshRef.current = null
      detach?.()
    }
  }, [chart, clear])

  return { ...state, clear }
}
